import AttachFileIcon from "@mui/icons-material/AttachFile";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import { Box, Link, Paper, Typography, alpha } from "@mui/material";
import type React from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { TestId } from "../../constants/testIds";
import type translations from "../../translations/translations.json";
import {
  type Order,
  OrderStatus,
  formatOrderLocations,
  isOrderApproved,
  splitDepartureTimestamp,
} from "../../types/order";
import { buildOrderExpenseChecklistItems } from "../../utils/buildOrderExpenseChecklistItems";
import { formatLicensePlateInput } from "../../utils/formatLicensePlate";
import { getActiveRevisionNote } from "../../utils/orderRevisionNotes";
import { getOrderStatusAccentColor } from "../../utils/orderStatusAccent";
import DetailField from "../common/DetailField";
import OrderStatusChip from "../common/OrderStatusChip";
import UserAvatar from "../common/UserAvatar";
import ExpenseChecklist from "../uangJalanReports/ExpenseChecklist";
import OrderTotalsSummary from "../uangJalanReports/OrderTotalsSummary";
import ReportOrderCardHeader, {
  type ReportCardMetaItem,
} from "../uangJalanReports/ReportOrderCardHeader";
import OrderProofAttachments from "./OrderProofAttachments";

type TranslationKeys = keyof typeof translations.en;

export interface OrderDetailPanelChecklistConfig {
  disabled: boolean;
  disabledHint?: string;
  onToggle?: (orderId: string, itemKey: string, checked: boolean) => void;
  loading?: boolean;
}

export interface OrderDetailPanelLinkConfig {
  type: "report" | "order-detail";
  openInNewTab?: boolean;
}

interface OrderDetailPanelProps {
  order: Order;
  variant?: "card" | "flat";
  checklist: OrderDetailPanelChecklistConfig;
  secondaryLink?: OrderDetailPanelLinkConfig;
  financeActions?: React.ReactNode;
  showOverrideNotes?: boolean;
  statusTestId?: string;
  rootTestId?: string;
  expenseChecklistTestId?: string;
}

const notePaperSx = {
  mt: 1.5,
  p: 1.5,
  borderRadius: 1.5,
  bgcolor: (theme: any) => alpha(theme.palette.warning.main, 0.1),
  borderColor: "warning.main",
};

const OrderDetailPanel: React.FC<OrderDetailPanelProps> = ({
  order,
  variant = "flat",
  checklist,
  secondaryLink,
  financeActions,
  showOverrideNotes = false,
  statusTestId,
  rootTestId,
  expenseChecklistTestId,
}) => {
  const { t } = useTranslation();
  const checklistItems = buildOrderExpenseChecklistItems(order, t);
  const activeRevisionNote = getActiveRevisionNote(order);
  const isCard = variant === "card";
  const orderDetailLink = secondaryLink?.type === "order-detail";
  const checklistDisabledForOrderStatus = order.status !== OrderStatus.IN_REVIEW;
  const checklistDisabled = checklist.disabled || checklistDisabledForOrderStatus;
  const checklistDisabledHint = checklistDisabledForOrderStatus
    ? t("reports.checklistDisabledOrderStatus" as TranslationKeys)
    : checklist.disabledHint;
  const cardRegularChecklistDisabledHint = checklistDisabledForOrderStatus
    ? undefined
    : checklist.disabledHint;

  const reportUrl = order.uangJalanReportReadableId
    ? `/uang-jalan-reports/${order.uangJalanReportReadableId}`
    : null;
  const orderDetailUrl = `/orders/${order.readableId}`;

  const driverName = order.driver?.name || "—";

  const readableIdSx = {
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums" as const,
    letterSpacing: "-0.01em",
  };

  const operationalNoteField = order.note ? (
    <DetailField
      label={t("orders.note" as TranslationKeys)}
      value={
        <Box component="span" sx={{ whiteSpace: "pre-wrap", display: "block" }}>
          {order.note}
        </Box>
      }
    />
  ) : null;

  const metadataColumn = (
    <Box sx={{ minWidth: 0 }}>
      <Box
        sx={{
          display: "grid",
          gap: { xs: 1.5, sm: 2 },
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
        }}
      >
        <Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
            {t("Status" as TranslationKeys)}
          </Typography>
          <OrderStatusChip status={order.status} data-testid={statusTestId} />
        </Box>
        <DetailField
          label={t("Team" as TranslationKeys)}
          value={order.teamOperations?.name || "—"}
        />
        <DetailField
          label={t("Departure" as TranslationKeys)}
          value={order.departureTimestamp || "—"}
        />
        <DetailField
          label={t("License Plate" as TranslationKeys)}
          value={
            order.truck?.licensePlate ? formatLicensePlateInput(order.truck.licensePlate) : "—"
          }
        />
        <DetailField
          label={t("DRIVER" as TranslationKeys)}
          value={
            order.driver ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <UserAvatar
                  user={{
                    _id: order.driver._id,
                    name: order.driver.name,
                    profilePictureUrl: order.driver.profilePictureUrl,
                  }}
                  size={28}
                />
                <Typography variant="body2" component="span">
                  {driverName}
                </Typography>
              </Box>
            ) : (
              "—"
            )
          }
        />
        <DetailField label={t("Customer" as TranslationKeys)} value={order.customer?.name || "—"} />
        <DetailField
          label={t("No. LT/POD" as TranslationKeys)}
          value={order.externalOrderId || "—"}
        />
        {operationalNoteField ? (
          <Box sx={{ gridColumn: { sm: "1 / -1" } }} data-testid={TestId.ORDER.NOTE}>
            {operationalNoteField}
          </Box>
        ) : null}
        <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
          <DetailField
            label={t("orders.load" as TranslationKeys)}
            value={formatOrderLocations(order.load)}
          />
        </Box>
        <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
          <DetailField
            label={t("orders.unload" as TranslationKeys)}
            value={formatOrderLocations(order.unload)}
          />
        </Box>
        {(order.proofAttachments?.length ?? 0) > 0 ? (
          <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
            <DetailField
              label={t("orders.proofImage" as TranslationKeys)}
              value={<OrderProofAttachments attachments={order.proofAttachments ?? []} />}
            />
          </Box>
        ) : null}
      </Box>

      {secondaryLink?.type === "report" && reportUrl && (
        <Link
          component={RouterLink}
          to={reportUrl}
          underline="hover"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            mt: 2,
            fontSize: "body2.fontSize",
          }}
          data-testid={TestId.ORDER.VIEW_REPORT}
        >
          {t("orders.viewUangJalanReportWithId" as TranslationKeys, {
            readableId: order.uangJalanReportReadableId,
          })}
          <OpenInNewIcon sx={{ fontSize: 16 }} />
        </Link>
      )}
    </Box>
  );

  const checklistColumn = (
    <Box
      sx={{ minWidth: 0, width: "100%" }}
      data-testid={expenseChecklistTestId ?? TestId.ORDER.EXPENSE_CHECKLIST}
    >
      <ExpenseChecklist
        orderId={order._id}
        items={checklistItems}
        section="all"
        columns={1}
        onToggle={checklist.onToggle}
        disabled={checklistDisabled}
        disabledHint={checklistDisabledHint}
        loading={checklist.loading}
      />
    </Box>
  );

  const regularChecklistColumn = (
    <Box
      sx={{ minWidth: 0, width: "100%" }}
      data-testid={expenseChecklistTestId ?? TestId.ORDER.EXPENSE_CHECKLIST}
    >
      <ExpenseChecklist
        orderId={order._id}
        items={checklistItems}
        section="standard"
        columns={2}
        onToggle={checklist.onToggle}
        disabled={checklistDisabled}
        disabledHint={cardRegularChecklistDisabledHint}
        loading={checklist.loading}
      />
    </Box>
  );

  const additionalChecklistColumn = (
    <Box sx={{ minWidth: 0, width: "100%" }}>
      <ExpenseChecklist
        orderId={order._id}
        items={checklistItems}
        section="additional"
        columns={1}
        onToggle={checklist.onToggle}
        disabled={checklistDisabled}
        loading={checklist.loading}
      />
    </Box>
  );

  const totalsColumn = (
    <Box sx={{ minWidth: 0, width: "100%" }}>
      <OrderTotalsSummary order={order} />
    </Box>
  );

  const loadText = order.load?.length ? formatOrderLocations(order.load) : "";
  const unloadText = order.unload?.length ? formatOrderLocations(order.unload) : "";
  const routeText = [loadText, unloadText].filter(Boolean).join(" → ");

  const cardMetaItems: ReportCardMetaItem[] = [];
  const driverText = order.driver?.name ?? "";
  const vehiclePlate = order.truck?.licensePlate
    ? formatLicensePlateInput(order.truck.licensePlate)
    : "";
  const vehicleText = [driverText, vehiclePlate].filter(Boolean).join(" - ");
  if (vehicleText) {
    cardMetaItems.push({
      key: "vehicle",
      icon: (
        <LocalShippingOutlinedIcon
          fontSize="small"
          sx={{ color: "text.disabled", flexShrink: 0 }}
        />
      ),
      label: [t("DRIVER" as TranslationKeys), t("License Plate" as TranslationKeys)]
        .filter(Boolean)
        .join(" · "),
      value: (
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
          {vehicleText}
        </Typography>
      ),
    });
  }
  const departureDate = order.departureTimestamp
    ? splitDepartureTimestamp(order.departureTimestamp).date
    : "";
  if (departureDate) {
    cardMetaItems.push({
      key: "departure",
      icon: <EventOutlinedIcon fontSize="small" sx={{ color: "text.disabled", flexShrink: 0 }} />,
      label: t("Departure" as TranslationKeys),
      value: (
        <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }} noWrap>
          {departureDate}
        </Typography>
      ),
    });
  }
  if (order.customer?.name) {
    cardMetaItems.push({
      key: "customer",
      icon: <PersonOutlineIcon fontSize="small" sx={{ color: "text.disabled", flexShrink: 0 }} />,
      label: t("Customer" as TranslationKeys),
      value: (
        <Typography variant="body2" noWrap>
          {order.customer.name}
        </Typography>
      ),
    });
  }
  if (routeText) {
    cardMetaItems.push({
      key: "route",
      icon: <RouteOutlinedIcon fontSize="small" sx={{ color: "text.disabled", flexShrink: 0 }} />,
      label: `${t("orders.load" as TranslationKeys)} → ${t("orders.unload" as TranslationKeys)}`,
      value: <Typography variant="body2">{routeText}</Typography>,
    });
  }
  if ((order.proofAttachments?.length ?? 0) > 0) {
    cardMetaItems.push({
      key: "proof",
      icon: <AttachFileIcon fontSize="small" sx={{ color: "text.disabled", flexShrink: 0 }} />,
      label: t("orders.proofImage" as TranslationKeys),
      value: <OrderProofAttachments attachments={order.proofAttachments ?? []} layout="inline" />,
    });
  }

  const cardHeader = (
    <ReportOrderCardHeader
      testId={TestId.ORDER.CARD_HEADER}
      idSlot={
        orderDetailLink ? (
          <Link
            href={orderDetailUrl}
            target={secondaryLink?.openInNewTab ? "_blank" : undefined}
            rel={secondaryLink?.openInNewTab ? "noopener noreferrer" : undefined}
            underline="hover"
            data-testid={TestId.UJR.VIEW_ORDER(order._id)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              color: "primary.main",
              ...readableIdSx,
              fontSize: "subtitle1.fontSize",
              lineHeight: 1.4,
            }}
          >
            <Box component="span" data-testid={TestId.ORDER.READABLE_ID}>
              {order.readableId}
            </Box>
            <OpenInNewIcon sx={{ fontSize: 18 }} aria-hidden />
          </Link>
        ) : (
          <Typography variant="subtitle1" data-testid={TestId.ORDER.READABLE_ID} sx={readableIdSx}>
            {order.readableId}
          </Typography>
        )
      }
      secondarySlot={
        order.externalOrderId ? (
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid={TestId.ORDER.CARD_EXTERNAL_ID}
            sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}
          >
            {order.externalOrderId}
          </Typography>
        ) : undefined
      }
      statusSlot={<OrderStatusChip status={order.status} data-testid={statusTestId} />}
      actionsSlot={financeActions}
      meta={cardMetaItems}
    />
  );

  const content = (
    <Box data-testid={variant === "flat" ? rootTestId : undefined}>
      {isCard ? cardHeader : null}

      {isCard ? (
        <Box
          sx={{
            display: "grid",
            width: "100%",
            gap: 1.5,
            alignItems: "start",
            gridTemplateColumns: {
              xs: "1fr",
              lg: "minmax(0, 1.9fr) minmax(0, 1fr) 260px",
            },
          }}
        >
          {regularChecklistColumn}
          {additionalChecklistColumn}
          {totalsColumn}
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            gap: 2,
            alignItems: { lg: "flex-start" },
            width: "100%",
          }}
        >
          <Box
            sx={{
              flex: { lg: "1 1 252px" },
              minWidth: 0,
              width: { xs: "100%", lg: "auto" },
            }}
          >
            {metadataColumn}
          </Box>
          <Box
            sx={{
              flex: { lg: "1 1 312px" },
              minWidth: 0,
              width: { xs: "100%", lg: "auto" },
            }}
          >
            {checklistColumn}
          </Box>
          <Box
            sx={{
              flex: { lg: "0 0 240px" },
              minWidth: 0,
              width: { xs: "100%", lg: 240 },
            }}
          >
            {totalsColumn}
          </Box>
        </Box>
      )}

      {showOverrideNotes && isOrderApproved(order.status) && order.overrideNotes && (
        <Paper variant="outlined" sx={notePaperSx} data-testid={TestId.ORDER.OVERRIDE_NOTES}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            <Box component="span" sx={{ fontWeight: 700, color: "warning.dark", mr: 0.5 }}>
              {t("orders.overrideNotes" as TranslationKeys)}:
            </Box>
            {order.overrideNotes}
          </Typography>
        </Paper>
      )}

      {order.status === OrderStatus.REVISION_REQUIRED && activeRevisionNote && (
        <Paper variant="outlined" sx={notePaperSx} data-testid={TestId.ORDER.REVISION_NOTES}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            <Box component="span" sx={{ fontWeight: 700, color: "warning.dark", mr: 0.5 }}>
              {t("orders.revisionNote" as TranslationKeys)}:
            </Box>
            {activeRevisionNote}
          </Typography>
        </Paper>
      )}
    </Box>
  );

  if (variant === "card") {
    const accentColor = getOrderStatusAccentColor(order.status);
    return (
      <Paper
        elevation={0}
        data-testid={rootTestId}
        sx={{
          px: { xs: 1.5, sm: 2 },
          py: { xs: 1.25, sm: 1.5 },
          borderRadius: 2,
          border: 1,
          borderColor: "divider",
          borderLeft: `4px solid ${accentColor}`,
          boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
          transition: "box-shadow 0.15s ease",
          "&:hover": {
            boxShadow: "0 4px 12px rgba(15, 23, 42, 0.08)",
          },
        }}
      >
        {content}
      </Paper>
    );
  }

  return content;
};

export default OrderDetailPanel;
